
import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConversationList } from "@/features/messages/components/ConversationList";
import { ChatWindow } from "@/features/messages/components/ChatWindow";
import { ThreadingControls } from "@/features/messages/components/ThreadingControls";
import { UnifiedInboxView } from "@/features/messages/components/UnifiedInboxView";
import { useEmailConversations } from "@/features/messages/hooks/useEmailConversations";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { Inbox as InboxIcon, MessageSquare } from 'lucide-react';
import { Conversation } from "@/features/messages/types";

const Inbox = () => {
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const { data: emailConversations = [], isLoading } = useEmailConversations();

  // Transform EmailConversation[] to Conversation[]
  const conversations: Conversation[] = emailConversations.map(emailConv => ({
    id: emailConv.id,
    name: emailConv.subject || "No Subject",
    avatar: undefined,
    initials: emailConv.participants?.[0]?.name?.charAt(0).toUpperCase() || "?",
    lastMessage: emailConv.subject || "No messages",
    timestamp: new Date(emailConv.last_message_date).toISOString(),
    unread: emailConv.unread_count > 0,
    participantEmails: emailConv.participants?.map(p => p.email) || [],
    status: 'open',
    overallPriorityScore: null
  }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading conversations...</div>
      </div>
    );
  }

  return (
    <div className="h-full">
      <Tabs defaultValue="unified" className="h-full flex flex-col">
        <div className="border-b px-4 py-2">
          <TabsList>
            <TabsTrigger value="unified" className="flex items-center gap-2">
              <InboxIcon className="h-4 w-4" />
              Unified Inbox
            </TabsTrigger>
            <TabsTrigger value="conversations" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Conversations
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="unified" className="flex-1 m-0">
          <div className="h-full p-4">
            <UnifiedInboxView />
          </div>
        </TabsContent>

        <TabsContent value="conversations" className="flex-1 m-0">
          <ResizablePanelGroup direction="horizontal" className="h-full">
            <ResizablePanel defaultSize={30} minSize={25} maxSize={50}>
              <div className="h-full flex flex-col">
                <div className="p-4">
                  <ThreadingControls />
                </div>
                <div className="flex-1 overflow-hidden">
                  <ConversationList
                    conversations={conversations}
                    selectedConversationId={selectedConversationId}
                    onSelectConversation={setSelectedConversationId}
                  />
                </div>
              </div>
            </ResizablePanel>
            
            <ResizableHandle />
            
            <ResizablePanel defaultSize={70}>
              {selectedConversationId ? (
                <ChatWindow conversationId={selectedConversationId} />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Select a conversation</h3>
                    <p className="text-sm text-gray-500">
                      Choose a conversation from the list to start reading messages
                    </p>
                  </div>
                </div>
              )}
            </ResizablePanel>
          </ResizablePanelGroup>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Inbox;
